import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { generateClient } from 'aws-amplify/data'
import { listAll } from '@utils/listAll'
import type { Schema } from '../../amplify/data/resource'

const client = generateClient<Schema>()

export const useGroupsStore = defineStore('groups', () => {
	// State
	const groups = ref<any[]>([])
	const loading = ref(false)
	const error = ref<string | null>(null)
	const lastFetched = ref<Date | null>(null)
	const cacheTimeout = 5 * 60 * 1000 // 5 minutes

	// Computed
	const isStale = computed(() => {
		if (!lastFetched.value) return true
		return Date.now() - lastFetched.value.getTime() > cacheTimeout
	})

	const adminGroups = computed(() => 
		groups.value.filter(group => group.isAdmin === true)
	)

	const regularGroups = computed(() => 
		groups.value.filter(group => group.isAdmin !== true)
	)

	// Actions
	const fetchGroups = async (force = false) => {
		if (!force && !isStale.value && groups.value.length > 0) {
			return groups.value
		}

		loading.value = true
		error.value = null

		try {
			// Name-ascending from the same GSI the Groups table pages through, so ordering comes from
			// the server here too. This store feeds dropdowns, which must show every group, hence
			// reading every page — a bare `list()` returns only the first.
			groups.value = await listAll(({ limit, nextToken }) =>
				client.models.Group.listGroupByModelNameAndNameKey(
					{ modelName: 'Group' },
					{ sortDirection: 'ASC', limit, nextToken },
				))
			lastFetched.value = new Date()
			
			return groups.value
		} catch (err) {
			console.error('Error fetching groups:', err)
			error.value = err instanceof Error ? err.message : 'Failed to fetch groups'
			throw err
		} finally {
			loading.value = false
		}
	}

	const invalidateCache = () => {
		lastFetched.value = null
		groups.value = []
	}

	const refreshCache = async () => {
		return await fetchGroups(true)
	}

	return {
		// State
		groups,
		loading,
		error,
		lastFetched,

		// Computed
		isStale,
		adminGroups,
		regularGroups,

		// Actions
		fetchGroups,
		invalidateCache,
		refreshCache
	}
})
